import DownArrow from "@redotech/redo-icons/arrow-down_filled.svg";
import UpArrow from "@redotech/redo-icons/arrow-up_filled.svg";
import HelpCircleIcon from "@redotech/redo-icons/help-circle.svg";
import MinusIcon from "@redotech/redo-icons/minus.svg";
import { memo, ReactNode } from "react";
import { ButtonSize, IconButton } from "../../button";
import { Flex } from "../../flex";
import DownArrowIcon from "../../icon-old/down-arrow.svg";
import ExternalLinkIcon from "../../icon-old/external-link.svg";
import UpArrowIcon from "../../icon-old/up-arrow.svg";
import { SkeletonText } from "../../skeleton";
import { Header, Text } from "../../text";
import {
  BorderColorValue,
  BoxBorderWidthProps,
  BoxRadiusProps,
  SpacingValue,
} from "../../theme/box";
import { TextColorValue } from "../../theme/typography";
import {
  RedoMetricTooltip,
  RedoMetricTooltipProps,
} from "../../tooltip/metric-tooltip";
import { RedoBadge, RedoBadgeProps } from "../badge/redo-badge";
import * as redoMetricItemCss from "./redo-metric-item.module.css";

export interface SecondaryMetric {
  title: string;
  value: number | string;
}

interface RedoMetricItemInputProps {
  title: string;
  description?: ReactNode;
  value: number | string;
  valueColor?: TextColorValue;
  valueTitleType?: TitleType;
  /**
   * Optional inline node rendered to the right of the value on the same
   * row. Hidden while `pending` so it doesn't sit next to a skeleton.
   * Use for small secondary stats (e.g. a `RedoBadge`) that belong with
   * the headline number — not for the trend chip, which has its own
   * `comparisonChange` slot below.
   */
  valueTrailing?: ReactNode;
  /**
   * Rendered at the right edge of the title row (before the help icon).
   * Use for contextual chips or small actions that belong to the card but
   * shouldn't crowd the headline value.
   */
  headerTrailing?: ReactNode;
  explanationTooltipProps?: RedoMetricTooltipProps | undefined;
  href?: string;
  icon?: ReactNode;
  comparisonChange?: number;
  comparisonText?: string;
  /** Formats `comparisonChange` only — `value` arrives pre-formatted. */
  comparisonMetricType?: MetricType;
  isGoodChange?: boolean;
  changeBadge?: number;
  pending?: boolean;
  type?: "simple" | "metricFirst";
  secondaryMetrics?: SecondaryMetric[];
  secondaryMetricsPending?: boolean;
  dataTestId?: string;
  metricContainerStyling?: {
    padding?: SpacingValue;
    gap?: SpacingValue;
    borderRadius?: BoxRadiusProps;
    borderWidths?: BoxBorderWidthProps;
    borderColor?: BorderColorValue;
  };
}

const NUMBER_FORMAT = new Intl.NumberFormat();

export enum TitleType {
  Text = "text",
  Header = "header",
}

export enum MetricType {
  PERCENT = "percent",
  /** Difference between two percentages. Rendered " pp", never "%". */
  PERCENTAGE_POINTS = "percentagePoints",
  CURRENCY = "currency",
  UNIT = "unit",
}

export const MetricItemHeading = memo(function MetricItemHeading({
  title,
  href,
  tooltipProps,
  badgeProps,
  icon,
  trailing,
}: {
  title: string;
  href?: string;
  tooltipProps?: RedoMetricTooltipProps;
  badgeProps?: RedoBadgeProps;
  icon?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <Flex align="center" justify="space-between">
      <Flex dir="column" gap="lg" justify="center">
        {icon && <MetricItemIcon icon={icon} />}
        <Flex align="center" gap="xs">
          <Text fontSize="sm" fontWeight="medium" textColor="tertiary">
            {title}
          </Text>
          {badgeProps && <RedoBadge {...badgeProps} />}
        </Flex>
      </Flex>
      {(trailing || tooltipProps || href) && (
        <Flex align="center" alignSelf="flex-start" gap="xs">
          {trailing}
          {tooltipProps && <MetricItemHelpIcon tooltipProps={tooltipProps} />}
          {href && <MetricItemExternalLink href={href} />}
        </Flex>
      )}
    </Flex>
  );
});

const MetricItemDescription = memo(function MetricItemDescription({
  description,
}: {
  description: ReactNode;
}) {
  return (
    <Flex align="center" justify="space-between">
      {typeof description === "string" ? (
        <Text fontSize="xs" textColor="secondary">
          {description}
        </Text>
      ) : (
        description
      )}
    </Flex>
  );
});

export const MetricItemHelpIcon = memo(function MetricItemHelpIcon({
  tooltipProps,
}: {
  tooltipProps: RedoMetricTooltipProps;
}) {
  return (
    <RedoMetricTooltip {...tooltipProps}>
      <IconButton size={ButtonSize.SMALL}>
        <HelpCircleIcon className={redoMetricItemCss.helpCircleIcon} />
      </IconButton>
    </RedoMetricTooltip>
  );
});

const MetricItemExternalLink = memo(function MetricItemExternalLink({
  href,
}: {
  href: string;
}) {
  return (
    <IconButton
      className={redoMetricItemCss.iconButton}
      onClick={() => {
        location.href = href;
      }}
    >
      <ExternalLinkIcon className={redoMetricItemCss.externalLinkIcon} />
    </IconButton>
  );
});

const MetricItemIcon = memo(function MetricItemIcon({
  icon,
}: {
  icon: ReactNode;
}) {
  return <div>{icon}</div>;
});

const MetricItemValue = memo(function MetricItemValue({
  value,
  valueColor,
  valueTitleType = TitleType.Text,
  dataTestId,
}: {
  value: number | string;
  valueColor?: TextColorValue;
  valueTitleType?: TitleType;
  dataTestId?: string;
}) {
  return valueTitleType === TitleType.Header ? (
    <Header
      data-testid={dataTestId}
      fontSize="sm"
      fontWeight="medium"
      textColor={valueColor ?? "tertiary"}
    >
      {typeof value === "number" ? NUMBER_FORMAT.format(value) : value}
    </Header>
  ) : (
    <Text
      data-testid={dataTestId}
      fontSize="xl"
      fontWeight="bold"
      textColor={valueColor ?? "primary"}
    >
      {typeof value === "number" ? NUMBER_FORMAT.format(value) : value}
    </Text>
  );
});

const MetricItemChangeBadge = memo(function MetricItemChangeBadge({
  change,
  isGoodChange,
}: {
  change: number;
  isGoodChange?: boolean;
}) {
  const isPositiveChange = change > 0;
  const color =
    isGoodChange === undefined
      ? isPositiveChange
        ? "success"
        : "error"
      : isGoodChange
        ? "success"
        : "error";

  return (
    <RedoBadge
      color={color}
      segmentLeading={
        isPositiveChange
          ? { type: "icon", Icon: UpArrow }
          : { type: "icon", Icon: DownArrow }
      }
      size="md"
      text={NUMBER_FORMAT.format(change) + "%"}
    />
  );
});

export const MetricItemStatAndTextComparison = memo(
  function MetricItemPeriodComparison({
    change,
    text,
    isGoodChange,
    metricType = MetricType.PERCENT,
    dataTestId,
  }: {
    change: number;
    text?: string | undefined;
    isGoodChange?: boolean;
    metricType?: MetricType;
    dataTestId?: string;
  }) {
    const prefix = metricType === MetricType.CURRENCY ? "$" : "";
    const suffix =
      metricType === MetricType.PERCENT
        ? "%"
        : metricType === MetricType.PERCENTAGE_POINTS
          ? " pp"
          : "";
    return (
      <Flex align="center" data-testid={dataTestId} gap="xs">
        <Flex
          align="center"
          className={
            isGoodChange ? redoMetricItemCss.green : redoMetricItemCss.red
          }
          gap="xxs"
        >
          {change > 0 ? (
            <UpArrowIcon className={redoMetricItemCss.changeIcon} />
          ) : change < 0 ? (
            <DownArrowIcon className={redoMetricItemCss.changeIcon} />
          ) : (
            <MinusIcon className={redoMetricItemCss.changeIcon} />
          )}
          <Text
            data-testid={dataTestId ? `${dataTestId}-change` : undefined}
            fontSize="xs"
          >
            {prefix}
            {Math.round(change * 100) / 100}
            {suffix}
          </Text>
        </Flex>
        {text && (
          <Text
            color="tertiary"
            data-testid={dataTestId ? `${dataTestId}-text` : undefined}
            fontSize="xs"
          >
            {text}
          </Text>
        )}
      </Flex>
    );
  },
);

export interface MetricItemContainerStylingProps {
  padding?: SpacingValue;
  gap?: SpacingValue;
  borderRadius?: BoxRadiusProps;
  borderWidths?: BoxBorderWidthProps;
}

const MetricItemSecondaryMetrics = memo(function MetricItemSecondaryMetrics({
  secondaryMetrics,
  pending,
  padding,
}: {
  secondaryMetrics?: SecondaryMetric[];
  pending?: boolean;
  padding?: SpacingValue;
}) {
  if (!secondaryMetrics || secondaryMetrics.length === 0) {
    return null;
  }

  return (
    <Flex dir="column" gap="none">
      <div className={redoMetricItemCss.divider} />
      <MetricItemContent>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${secondaryMetrics.length}, 1fr)`,
            gap: "16px",
          }}
        >
          {secondaryMetrics.map((metric, index) => (
            <Flex align="flex-start" dir="column" gap="xs" key={index}>
              <Text fontSize="xs" textColor="secondary">
                {metric.title}
              </Text>
              {pending ? (
                <SkeletonText height="16px" length={8} />
              ) : (
                <Text fontSize="sm" fontWeight="bold" textColor="primary">
                  {typeof metric.value === "number"
                    ? NUMBER_FORMAT.format(metric.value)
                    : metric.value}
                </Text>
              )}
            </Flex>
          ))}
        </div>
      </MetricItemContent>
    </Flex>
  );
});

export const MetricItemContainer = memo(function MetricItemContainer({
  children,
  borderRadius,
  borderWidths,
  borderColor,
  dataTestId,
  "data-arbiter": dataArbiter,
}: {
  children: ReactNode;
  borderRadius?: BoxRadiusProps;
  borderWidths?: BoxBorderWidthProps;
  borderColor?: BorderColorValue;
  dataTestId?: string;
  "data-arbiter"?: string;
}) {
  return (
    <Flex
      borderBottomWidth={borderWidths?.borderBottomWidth}
      borderColor={borderColor}
      borderLeftWidth={borderWidths?.borderLeftWidth}
      borderRightWidth={borderWidths?.borderRightWidth}
      borderTopWidth={borderWidths?.borderTopWidth}
      borderWidth={borderWidths ? borderWidths?.borderWidth : "1px"} // Preserves existing behavior to have '1px' width by default while also not requiring "borderWidth": "none" every time you want to specify a specific width
      bottomLeftRadius={borderRadius?.bottomLeftRadius}
      bottomRightRadius={borderRadius?.bottomRightRadius}
      className={redoMetricItemCss.container}
      data-arbiter={dataArbiter}
      data-testid={dataTestId}
      dir="column"
      gap="none"
      radius={borderRadius ? borderRadius?.radius : "sm"} // Preserves existing behavior to have 'sm' radius by default while also not requiring "radius": "none" every time you want to specify a specific rounding
      topLeftRadius={borderRadius?.topLeftRadius}
      topRightRadius={borderRadius?.topRightRadius}
    >
      {children}
    </Flex>
  );
});

export const MetricItemContent = memo(function MetricItemContent({
  children,
  padding = "xl",
  gap = "lg",
}: {
  children: ReactNode;
  padding?: SpacingValue;
  gap?: SpacingValue;
}) {
  return (
    <Flex dir="column" gap={gap} p={padding}>
      {children}
    </Flex>
  );
});

// Don't use href and explanation text together
export const RedoMetricItem = memo(function RedoMetricItem({
  title,
  valueTitleType = TitleType.Text,
  description,
  value,
  valueColor,
  valueTrailing,
  headerTrailing,
  explanationTooltipProps,
  changeBadge,
  href,
  icon,
  comparisonChange,
  comparisonText,
  comparisonMetricType,
  isGoodChange, // determines green or red color if change is good or bad, sometimes a positive change can be a bad thing, sometimes it can be good
  pending,
  type = "simple",
  secondaryMetrics,
  secondaryMetricsPending,
  dataTestId,
  metricContainerStyling,
}: RedoMetricItemInputProps) {
  const valueTestId = dataTestId ? `${dataTestId}-value` : undefined;
  const containerTestId = dataTestId ? `metric-${dataTestId}` : undefined;
  const comparisonTestId = dataTestId ? `${dataTestId}-comparison` : undefined;

  const showSecondaryMetric =
    comparisonChange !== undefined || secondaryMetricsPending !== undefined;

  return (
    <MetricItemContainer
      borderColor={metricContainerStyling?.borderColor}
      borderRadius={metricContainerStyling?.borderRadius}
      borderWidths={metricContainerStyling?.borderWidths}
      data-arbiter="RedoMetricItem"
      dataTestId={containerTestId}
    >
      <MetricItemContent
        gap={metricContainerStyling?.gap}
        padding={metricContainerStyling?.padding}
      >
        {href && <MetricItemExternalLink href={href} />}
        {type === "simple" && (
          <MetricItemHeading
            icon={icon}
            title={title}
            tooltipProps={explanationTooltipProps}
            trailing={headerTrailing}
          />
        )}
        <Flex justify="space-between">
          <Flex align="baseline" gap="sm">
            {pending ? (
              <SkeletonText height="30px" length={16} />
            ) : (
              <MetricItemValue
                dataTestId={valueTestId}
                value={value}
                valueColor={valueColor}
                valueTitleType={valueTitleType}
              />
            )}
            {!pending && valueTrailing}
          </Flex>
          {changeBadge && (
            <MetricItemChangeBadge
              change={changeBadge}
              isGoodChange={isGoodChange}
            />
          )}
        </Flex>
        {type === "metricFirst" && <MetricItemHeading title={title} />}
        {description && <MetricItemDescription description={description} />}
        {showSecondaryMetric &&
          (comparisonChange === undefined || secondaryMetricsPending ? (
            <SkeletonText height="16px" length={24} />
          ) : (
            <MetricItemStatAndTextComparison
              change={comparisonChange}
              dataTestId={comparisonTestId}
              isGoodChange={isGoodChange}
              metricType={comparisonMetricType}
              text={comparisonText}
            />
          ))}
      </MetricItemContent>
      <MetricItemSecondaryMetrics
        padding={metricContainerStyling?.padding}
        pending={pending}
        secondaryMetrics={secondaryMetrics}
      />
    </MetricItemContainer>
  );
});
